import { environment } from '@env/environment';
import { FeatureFlagKey, FeatureFlagType } from '@feature-flags/feature-flag.service';
import { HashMap } from '@utils/hash-map';
import { resolveActiveBaseDomain } from './base-domain';

/**
 * Passkeys belong to one domain at a time, so the Passkey Settings account-menu item is offered on
 * only one of them. The `hidePasskeySettings` flag says which:
 *   - `true`:  offered on the old domain only (passkeys still live on cintra.cloud)
 *   - `false`: offered on the new domain only (passkeys have moved to cintrapeople.com)
 * Flipping it on switchover day moves the item across without a deploy. A ConfigCat rule could not
 * express this -- targeting has no view of the host the app is served from.
 *
 * Fail-open: only an explicit boolean hides anything. A flag deleted from ConfigCat, never fetched,
 * or mistyped leaves the item visible everywhere, and so does a host on neither domain (an element
 * embedded on a third-party site).
 *
 * REMOVING THIS FEATURE (TO-4866, at the cintra.cloud switchover on 19 October 2026) -- delete, in
 * any order:
 *   1. this file and its spec
 *   2. `withoutHiddenPasskeySettings` call in `HeaderAdapterService` (main app header)
 *   3. `withoutHiddenPasskeySettings` calls in `src/elements/component-library/main.ts` (both the
 *      header build and the ConfigCat re-filter in the flag subscription)
 *   4. `'hidePasskeySettings'` from `FeatureFlagKey`
 *   5. `e2e/.../domain-migration/PasskeySettingsVisibility.feature` and its steps -- these assert
 *      the hiding itself, so they are the only tests that fail if the code goes and they stay
 *   6. the `hidePasskeySettings` pins in `e2e/.../app-layout/visual-regression/common.ts` and
 *      `e2e/.../elements/component-library/AccountModals.feature` -- inert once the code is gone
 *      (they pin a key nothing reads), so these are tidy-up rather than breakage
 */
export const HIDE_PASSKEY_SETTINGS_FLAG_KEY: FeatureFlagKey = 'hidePasskeySettings';

/**
 * Account-menu link path the API serves for Passkey Settings. Matched on `path` rather than `label`
 * because the label is display copy and can be reworded server-side without notice.
 */
export const PASSKEY_SETTINGS_MENU_PATH = '(modal:passkeys)';

/**
 * Whether the app is being served from the replacement domain
 * (`environment.domainMigration.newDomain`) or any subdomain of it.
 *
 * Counterpart to `DomainMigrationService.isOnOldDomain()`, kept as a plain function because the
 * elements bundle builds its header config outside Angular DI. Dev/e2e environments configure
 * `oldDomain === newDomain`, so both read `true` there.
 */
export function isOnNewDomain(hostname: string = location.hostname): boolean {
  const newDomain = environment.domainMigration?.newDomain;
  return !!newDomain && !!resolveActiveBaseDomain(hostname, [newDomain]);
}

/** Whether the app is being served from the retiring domain (`environment.domainMigration.oldDomain`) or a subdomain of it. */
export function isOnOldDomain(hostname: string = location.hostname): boolean {
  const oldDomain = environment.domainMigration?.oldDomain;
  return !!oldDomain && !!resolveActiveBaseDomain(hostname, [oldDomain]);
}

/**
 * A host on both domains (dev, e2e and CI, which configure one hostname as both) counts as the new
 * domain, so the flag still decides there: on hides the item, off shows it.
 */
export function arePasskeySettingsHidden(featureFlags: HashMap<FeatureFlagType> | undefined, hostname?: string): boolean {
  const offeredOnOldDomainOnly = featureFlags?.[HIDE_PASSKEY_SETTINGS_FLAG_KEY];
  if (typeof offeredOnOldDomainOnly !== 'boolean') {
    return false;
  }

  return offeredOnOldDomainOnly ? isOnNewDomain(hostname) : isOnOldDomain(hostname) && !isOnNewDomain(hostname);
}

export function isPasskeySettingsLink(link: { path?: string }): boolean {
  return link?.path === PASSKEY_SETTINGS_MENU_PATH;
}

/**
 * Drops the Passkey Settings item from an already-built account menu when it should be hidden.
 *
 * Applied to the built menu rather than to the raw API links so both hosts (the main app's
 * `HeaderAdapterService` and the elements bundle) share one filter regardless of how they map links
 * to menu items.
 */
export function withoutHiddenPasskeySettings<T extends { path?: string }>(
  menuItems: T[],
  featureFlags: HashMap<FeatureFlagType> | undefined,
  hostname?: string
): T[] {
  if (!arePasskeySettingsHidden(featureFlags, hostname)) {
    return menuItems;
  }

  return menuItems.filter((item) => !isPasskeySettingsLink(item));
}
